"use client";

import { useEffect, useRef } from "react";
import { useActionState } from "react";

import { Button } from "@/components/button/button.component";
import { FieldErrorMessage } from "@/components/formError/fieldErrorMessage.component";
import { formatUsPhoneNumber } from "@/components/phoneInput/phoneInput.component";
import { Text } from "@/components/text/text.component";

import {
  type LeadCaptureState,
  submitLeadCapture,
} from "@/app/actions/leadCapture";
import { EVENTS } from "@/constants/analytics.constants";
import { FORM_EVENTS } from "@/constants/formAnalytics.constants";
import { useFormSubmitAttemptTag } from "@/hooks/useFormSubmitAttemptTag";
import { useTagging } from "@/hooks/useTagging";

import { QUIZ_EMAIL_CAPTURE } from "../../quiz.constants";
import { AnswerProps } from "../quizFlow.types";
import { QuizQuestionHelperText } from "./quizQuestionHelperText.component";

const initialState: LeadCaptureState = { success: false };

export const LeadCapture = ({
  question,
  onLeadCaptureSubmit,
  onLeadCaptureSkip,
}: AnswerProps) => {
  const config = question.leadCaptureConfig;
  const [state, formAction, pending] = useActionState(
    submitLeadCapture,
    initialState,
  );
  const [, setTag] = useTagging();
  const isPendingRef = useRef(false);

  useFormSubmitAttemptTag(pending, FORM_EVENTS.QUIZ_EMAIL.formEvent);

  useEffect(() => {
    if (pending) {
      isPendingRef.current = true;
    }
  }, [pending]);

  useEffect(() => {
    if (!isPendingRef.current) return;
    isPendingRef.current = false;

    if (state.success && state.data) {
      setTag(QUIZ_EMAIL_CAPTURE);
      setTag({
        event: EVENTS.FORM_SUBMIT_COMPLETE,
        outcome: "success",
        formEvent: FORM_EVENTS.QUIZ_EMAIL.formEvent,
      });
      const { email = "", phone = "" } = state.data;
      onLeadCaptureSubmit({ email, phone });
    } else if (state.errors) {
      const firstErrorField = Object.keys(state.errors)[0] as
        | "email"
        | "phone"
        | undefined;
      const errorCode = firstErrorField
        ? state.errors[firstErrorField]
        : undefined;
      setTag({
        ...QUIZ_EMAIL_CAPTURE,
        formEvent: {
          ...QUIZ_EMAIL_CAPTURE.formEvent,
          fieldName: firstErrorField,
          errorCode,
        },
      });
      setTag({
        event: EVENTS.FORM_SUBMIT_FAILURE,
        formEvent: {
          ...FORM_EVENTS.QUIZ_EMAIL.formEvent,
          fieldName:
            firstErrorField === "email" ? "E_Mail_Address" : firstErrorField,
          errorCode,
        },
      });
    } else if (state.formError) {
      setTag({
        ...QUIZ_EMAIL_CAPTURE,
        formEvent: {
          ...QUIZ_EMAIL_CAPTURE.formEvent,
          errorCode: state.formError,
        },
      });
      setTag({
        event: EVENTS.FORM_SUBMIT_FAILURE,
        formEvent: {
          ...FORM_EVENTS.QUIZ_EMAIL.formEvent,
          errorCode: state.formError,
        },
      });
    }
  }, [state]);

  if (!config) return null;

  return (
    <div data-testid="lead-capture" className="mx-auto w-full max-w-2xl">
      <div className="flex flex-col items-center gap-6">
        <form
          data-testid="lead-capture-form"
          action={formAction}
          className="flex w-full flex-col gap-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label
                htmlFor="lead-capture-email"
                className="text-greyscale-headline text-sm font-medium">
                Email
                <span className="ml-0.5">*</span>
              </label>
              <input
                id="lead-capture-email"
                data-testid="lead-capture-email"
                type="email"
                name="email"
                placeholder="Email address"
                aria-invalid={state.errors?.email ? true : undefined}
                aria-describedby={
                  state.errors?.email ? "lead-capture-email-error" : undefined
                }
                className="border-greyscale-strength/30 focus:border-secondary rounded-full border px-5 py-3 text-sm transition-colors outline-none"
              />
              {state.errors?.email && (
                <FieldErrorMessage
                  id="lead-capture-email-error"
                  className="body-6 pl-2"
                  testId="lead-capture-email-error">
                  {state.errors.email}
                </FieldErrorMessage>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="lead-capture-phone"
                className="text-greyscale-headline text-sm font-medium">
                Phone number
                <span className="ml-0.5">*</span>
              </label>
              <input
                id="lead-capture-phone"
                data-testid="lead-capture-phone"
                type="tel"
                name="phone"
                inputMode="numeric"
                maxLength={14}
                placeholder="(555) 123-4567"
                aria-invalid={state.errors?.phone ? true : undefined}
                aria-describedby={
                  state.errors?.phone ? "lead-capture-phone-error" : undefined
                }
                onInput={(e) => {
                  e.currentTarget.value = formatUsPhoneNumber(
                    e.currentTarget.value,
                  );
                }}
                className="border-greyscale-strength/30 focus:border-secondary rounded-full border px-5 py-3 text-sm transition-colors outline-none"
              />
              {state.errors?.phone && (
                <FieldErrorMessage
                  id="lead-capture-phone-error"
                  className="body-6 pl-2"
                  testId="lead-capture-phone-error">
                  {state.errors.phone}
                </FieldErrorMessage>
              )}
            </div>
          </div>

          {state.formError && (
            <Text
              variant="body6"
              data-testid="lead-capture-form-error"
              role="alert"
              className="text-center text-red-500">
              {state.formError}
            </Text>
          )}

          <QuizQuestionHelperText text={question.helperText} />

          {config.legalText && (
            <Text
              variant="body6"
              data-testid="lead-capture-legal"
              className="text-greyscale-body text-center font-thin">
              {config.legalText}
            </Text>
          )}

          <div className="flex flex-col items-center gap-4">
            <Button
              secondary
              data-testid="lead-capture-submit"
              type="submit"
              loading={pending}
              loaderVariant="primary"
              className="px-8">
              {config.submitLabel}
            </Button>

            <button
              type="button"
              data-testid="lead-capture-skip"
              onClick={onLeadCaptureSkip}
              className="text-greyscale-body hover:text-secondary text-sm underline underline-offset-2">
              {config.skipLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
